import { modelUtils } from '@pelicargo/utils';

const { flattenThemeToCssVars } = modelUtils.customerTheme;

type CustomerTheme = modelUtils.customerTheme.CustomerTheme;

const FONT_LINK_ID = 'peli-tenant-font';

// The custom properties written by the last call, so the ones that disappear on the next call can
// be removed rather than left stale - clearing a field in the admin UI has to restore the static
// `var()` fallback baked into the Chakra theme, not keep the old override alive.
let applied: string[] = [];

/**
 * Pushes a tenant's theme overrides onto the page as `--peli-*` custom properties.
 *
 * Written to `document.documentElement.style` - the inline `style` attribute on `<html>` - rather
 * than a `<style>` element carrying a `:root` rule. An inline declaration wins the cascade
 * outright, with no ordering or specificity contest against Chakra's own `:host,:root` emotion
 * `<Global>` block, and `removeProperty` gives a clean per-key reset.
 *
 * Deliberately pure DOM: it is called from outside the React tree, because `ChakraProvider` sits
 * above `<TRPC>` and the theme object is built at module-eval time, so the fetched overrides can
 * never reach the theme itself.
 *
 * `flattenThemeToCssVars` omits absent values instead of emitting them empty, and that must not be
 * undone here: `--peli-x: ;` does not trigger the `var()` fallback - the property becomes
 * invalid-at-computed-value-time, which can render text transparent.
 */
export const applyThemeVars = (theme?: CustomerTheme) => {
  const vars = flattenThemeToCssVars(theme);
  const root = document.documentElement.style;

  for (const name of applied) {
    if (!(name in vars)) root.removeProperty(name);
  }

  for (const [name, value] of Object.entries(vars)) {
    root.setProperty(name, value);
  }

  applied = Object.keys(vars);
};

/**
 * Loads a tenant's webfont, idempotently. `fonts.url` is not a CSS variable: a `fonts.heading` /
 * `fonts.body` family override is inert unless the face is actually loaded, and `index.html` only
 * hardcodes the Inter + Ephesis Google Fonts link.
 *
 * Compares the raw attribute rather than `link.href`, which the DOM resolves to an absolute URL.
 */
export const ensureFontLink = (url?: string) => {
  const existing = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;

  if (!url) {
    existing?.remove();
    return;
  }

  if (existing) {
    if (existing.getAttribute('href') !== url) existing.setAttribute('href', url);
    return;
  }

  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);
};
